import type { BakeryNow, DayHours } from '@/types'

import { toFaDigits } from '@/lib/format'
import { OPENING_HOURS } from '@/constants/site'

const pad = (value: number): string => String(value).padStart(2, '0')

const parseKey = (key: string): { year: number, month: number, day: number } => {
    const [year, month, day] = key.split('-').map(Number)

    return { year, month, day }
}

export const toMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number)

    return hours * 60 + minutes
}

export const fromMinutes = (total: number): string => `${pad(Math.floor(total / 60))}:${pad(total % 60)}`

// "07:00" becomes "۷:۰۰ صبح", "17:30" becomes "۵:۳۰ عصر"
export const formatClock = (time: string): string => {
    const [hours, minutes] = time.split(':').map(Number)
    const suffix = hours < 12 ? 'صبح' : hours < 16 ? 'ظهر' : 'عصر'

    return toFaDigits(`${hours % 12 || 12}:${pad(minutes)}`) + ` ${suffix}`
}

export const weekdayOfKey = (key: string): number => {
    const { year, month, day } = parseKey(key)

    return new Date(Date.UTC(year, month - 1, day)).getUTCDay()
}

export const addDays = (key: string, amount: number): string => {
    const { year, month, day } = parseKey(key)
    const date = new Date(Date.UTC(year, month - 1, day + amount))

    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`
}

// Formats a YYYY-MM-DD key in Persian (Solar Hijri calendar, Persian digits)
// without ever shifting it through the viewer's own time zone.
export const formatDateKey = (key: string, options: Intl.DateTimeFormatOptions): string => {
    const { year, month, day } = parseKey(key)

    return new Intl.DateTimeFormat('fa-IR', { timeZone: 'UTC', ...options }).format(new Date(Date.UTC(year, month - 1, day)))
}

// The current date and time where the bakery is, whatever the visitor's own time zone.
export const getBakeryNow = (timezone: string, date: Date = new Date()): BakeryNow => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23'
    }).formatToParts(date)

    const read = (type: Intl.DateTimeFormatPartTypes): string => parts.find(part => part.type === type)?.value ?? '0'
    const dateKey = `${read('year')}-${read('month')}-${read('day')}`

    return {
        dateKey,
        weekday: weekdayOfKey(dateKey),
        minutes: (Number(read('hour')) % 24) * 60 + Number(read('minute'))
    }
}

// "07:00" becomes "۷ صبح", "17:30" becomes "۵:۳۰ عصر"
export const formatClockShort = (time: string): string => formatClock(time).replace(':۰۰ ', ' ')

export const getHoursFor = (weekday: number): DayHours | undefined => OPENING_HOURS.find(entry => entry.day === weekday)

export const formatRange = (hours: DayHours | undefined, short = false): string => {
    if (!hours?.open || !hours.close) return 'تعطیل'

    const format = short ? formatClockShort : formatClock

    return `${format(hours.open)} – ${format(hours.close)}`
}

// Groups consecutive days that share the same hours: "سه‌شنبه تا جمعه", "۷ صبح – ۶ عصر"
export const groupHours = (): Array<{ days: string, hours: string }> => {
    const groups: Array<{ first: DayHours, last: DayHours }> = []

    for (const day of OPENING_HOURS) {
        const current = groups[groups.length - 1]

        if (current && current.last.open === day.open && current.last.close === day.close) current.last = day
        else groups.push({ first: day, last: day })
    }

    return groups.map(({ first, last }) => ({
        days: first.day === last.day ? first.short : `${first.short} تا ${last.short}`,
        hours: formatRange(first, true)
    }))
}

export type OpenStatus = {
    isOpen: boolean
    headline: string
    detail: string
}

export const getOpenStatus = (now: BakeryNow): OpenStatus => {
    const today = getHoursFor(now.weekday)

    if (today?.open && today.close) {
        const opens = toMinutes(today.open)
        const closes = toMinutes(today.close)

        if (now.minutes >= opens && now.minutes < closes) {
            if (closes - now.minutes <= 30) return { isOpen: true, headline: 'به‌زودی می‌بندیم', detail: `باز تا ${formatClock(today.close)}` }

            return { isOpen: true, headline: 'الان باز هستیم', detail: `تا ${formatClock(today.close)}` }
        }

        if (now.minutes < opens) return { isOpen: false, headline: `باز می‌کنیم ساعت ${formatClock(today.open)}`, detail: 'امروز' }
    }

    for (let offset = 1; offset <= 7; offset++) {
        const key = addDays(now.dateKey, offset)
        const hours = getHoursFor(weekdayOfKey(key))

        if (hours?.open) {
            return {
                isOpen: false,
                headline: `باز می‌کنیم ساعت ${formatClock(hours.open)}`,
                detail: offset === 1 ? 'فردا' : hours.name
            }
        }
    }

    return { isOpen: false, headline: 'تعطیل', detail: 'به‌زودی سر بزنید' }
}
